import React, { useState, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';
import { triggerSms, GET_SMS_TEMPLATES } from '../../utils/smsHelper';
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_BODY, ADM_MODAL_FOOTER, ADM_MODAL_HEADER_DOT } from './admissionModalStyles';

const SendSMSModal = ({ show, onHide, student, client }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const SMS_LIMIT = 160;

  useEffect(() => {
    if (show) {
      setError(''); setSuccess(false); setSelectedTemplate('');
      setMessage(`Hello ${student?.student_name || 'Student'}, this is from Rizee College regarding your admission.`);
      fetchTemplates();
    }
  }, [show, student]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const result = await client.query({
        query: GET_SMS_TEMPLATES,
        variables: {
          institute_id: parseInt(Cookies.get('institutionid')),
          branch_id: parseInt(student?.branch_id || Cookies.get('branchid'))
        },
        fetchPolicy: 'network-only'
      });
      setTemplates(result.data.getSMSTemplates || []);
    } catch (err) {
      console.error("Failed to fetch SMS templates:", err);
    }
    setLoading(false);
  };

  const handleTemplateChange = (e) => {
    const id = e.target.value;
    setSelectedTemplate(id);
    const template = templates.find(t => t.template_id === id);
    if (template) {
      setMessage(template.template_text.replace(/{student_name}/g, student?.student_name || 'Student'));
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true); setError('');
    const ok = await triggerSms(client, {
      student, type: 'MANUAL',
      customData: { message, template_id: selectedTemplate || undefined }
    });
    if (ok) { setSuccess(true); } else { setError("Failed to send SMS. Please check logs."); }
    setSending(false);
  };

  const handleClose = () => { setSuccess(false); setError(''); onHide(); };

  return (
    <Modal show={show} onHide={handleClose} centered size="md" dialogClassName="af-premium-modal">
      <Modal.Header closeButton style={ADM_MODAL_HEADER}>
        <Modal.Title style={ADM_MODAL_TITLE}>
          <span style={ADM_MODAL_HEADER_DOT} />
          <i className="fal fa-comment-dots" />
          <span>
            Send Custom SMS
            <span style={ADM_MODAL_TITLE_SUB}>Pick a template or write your own message</span>
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ ...ADM_MODAL_BODY, maxHeight: '70vh', overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {error && (
          <div className="alert alert-danger d-flex align-items-center py-2 mb-3" style={{ borderRadius: '8px', fontSize: '13px' }}>
            <i className="fas fa-exclamation-circle mr-2" />{error}
          </div>
        )}

        {success ? (
          <div className="text-center py-4">
            <div style={{
              width: '64px', height: '64px', borderRadius: '18px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', boxShadow: '0 8px 20px rgba(16,185,129,0.3)'
            }}>
              <i className="fas fa-check" style={{ fontSize: '26px', color: '#fff' }} />
            </div>
            <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>SMS Sent!</h5>
            <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
              Message sent to <strong>{student?.student_name}</strong>
            </p>
            <button className="btn btn-outline-secondary" style={{ borderRadius: '10px', height: '40px', minWidth: '120px', fontWeight: '500' }} onClick={handleClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            {/* ── Recipient ── */}
            <div className="af-modal-student-summary">
              <div className="af-summary-avatar">
                {(student?.student_name || 'S')[0].toUpperCase()}
              </div>
              <div className="af-summary-text">
                <strong>{student?.student_name}</strong>
                <span className="text-muted" style={{ fontSize: '13px' }}>
                  <i className="fas fa-phone-alt mr-1" />{student?.student_mobile}
                </span>
              </div>
              <span style={{
                marginLeft: 'auto', padding: '4px 12px', borderRadius: '20px',
                background: '#eff6ff', color: '#1d4ed8',
                fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px'
              }}>
                <i className="fas fa-comment-dots" /> SMS
              </span>
            </div>

            {/* ── Template ── */}
            <Form.Group className="mb-3">
              <Form.Label className="af-field-label">Template (Optional)</Form.Label>
              {loading ? (
                <div className="d-flex align-items-center text-muted" style={{ gap: '8px', fontSize: '13px', height: '42px' }}>
                  <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                  Loading templates...
                </div>
              ) : (
                <Form.Control as="select" className="af-form-select" value={selectedTemplate} onChange={handleTemplateChange}>
                  <option value="">— Custom Message —</option>
                  {templates.map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </Form.Control>
              )}
            </Form.Group>

            {/* ── Message ── */}
            <Form.Group className="mb-0">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="af-field-label mb-0">Message</Form.Label>
                <small className={message.length > SMS_LIMIT ? 'text-danger font-weight-bold' : 'text-muted'}>
                  {message.length} chars{message.length > SMS_LIMIT ? ` (${Math.ceil(message.length / SMS_LIMIT)} SMS)` : ''}
                </small>
              </div>
              <Form.Control
                as="textarea"
                rows={5}
                className="af-form-textarea"
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>

      {!success && (
        <Modal.Footer style={ADM_MODAL_FOOTER}>
          <button className="af-cancel-btn" onClick={handleClose}>Cancel</button>
          <button className="af-save-btn" onClick={handleSend} disabled={sending || !message.trim()} style={{ minWidth: '130px' }}>
            {sending
              ? <><Spinner animation="border" size="sm" className="mr-2" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />Sending...</>
              : <><i className="fas fa-paper-plane mr-2" />Send SMS</>
            }
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default withApollo(SendSMSModal);
